import React, {useEffect, useRef, useState} from 'react';
import {Box, useTheme} from '@mui/joy';
import ReactGridLayout, { Responsive, WidthProvider } from 'react-grid-layout';
import GridBox from './gridBox';
import {Grid, GridAsset, Maybe} from "../../../codegen/graphql";
import _ from "lodash";
import {getGridItemDimensions} from "../../../utils/getGridItemDimensions";
import useMediaQuery from "@mui/system/useMediaQuery";
import {useMutation} from "@apollo/client";
import {UpdateGridItem} from "../../../graphql/mutations/updateGridItem";
import GridDoor from "./gridDoor";

const ResponsiveGridLayout = WidthProvider(Responsive);
const GRID_COLS = 12;

interface Props {
    gridViewId: string;
    isEditMode: boolean;
    grid: Grid
}

const GridContent = ({ gridViewId, isEditMode, grid }: Props) => {
    const theme = useTheme();
    // bei kleineren Viewports zeigen wir einfach alle GridItems untereinander an --> nur für größere Viewports optimiert
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const [editModalOpen, setEditModalOpen] = useState<string | null>(null);
    const [updateGridItemMutation] = useMutation(UpdateGridItem)
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(0);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const observer = new ResizeObserver(entries => {
            setContainerWidth(entries[0].contentRect.width);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const gridItems = _.get(grid, 'gridItemList.gridItems', [])
    const layoutFromGridItems = gridItems.map((item, index) => {
        return {
            title: item?.title,
            i: item?.id || index.toString(),
            x: isSmallScreen ? 0 : item?.config?.x || 0,
            y: isSmallScreen ? 0 : item?.config?.y || 0,
            gridItem: item,
            ...getGridItemDimensions(item, item?.gridAssetList?.total, containerWidth, GRID_COLS)
        }
    })

    const handleGridChange = (layout: ReactGridLayout.Layout[], item: ReactGridLayout.Layout) => {
        const updatedItem = layout.find(l => l.i === item.i);
        if (!updatedItem) return;

        updateGridItemMutation({
            variables: {
                id: updatedItem.i,
                input: {
                    config: {
                        x: updatedItem.x,
                        y: updatedItem.y,
                        h: updatedItem.h,
                        w: updatedItem.w,
                    }
                }
            },
            optimisticResponse: {
                updateGridItem: {
                    __typename: 'GridItem',
                    id: updatedItem.i,
                    config: {
                        x: updatedItem.x,
                        y: updatedItem.y,
                        h: updatedItem.h,
                        w: updatedItem.w,
                    }
                },
            },
            refetchQueries: ["GridDetail"]
        })
    }

    return (
        <Box ref={containerRef} flex={1} p={2}>
            <ResponsiveGridLayout
                layouts={{ lg: layoutFromGridItems }}
                // responsive option --> bräuchte Anpassungen im BE, weil dafür jede Viewport config gespeichert wird
                // breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                // cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
                draggableCancel=".no-drag"
                compactType={null}
                preventCollision={true}
                breakpoints={{ lg: 0 }}
                cols={{ lg: GRID_COLS }}
                rowHeight={160}
                isDraggable={isEditMode && !editModalOpen}
                isResizable={isEditMode}
                onResizeStop={(layout, item) => handleGridChange(layout, item)}
                onDragStop={(layout, item) => handleGridChange(layout, item)}
            >
                {
                    layoutFromGridItems.map((item, i) => {
                        const gridAssets: Maybe<GridAsset>[] = _.get(item, 'gridItem.gridAssetList.gridAssets', [])
                        const isVisible = _.get(item, 'gridItem.visible', false)
                        const itemType = _.get(item, 'gridItem.type')

                        return <Box key={item.i} sx={{display: !isVisible && !isEditMode ? "none" : "block"}}>
                            {
                                itemType === "DOOR" ?
                                    <GridDoor
                                        gridItemId={item?.gridItem?.id}
                                        isEditMode={isEditMode}
                                    />
                                :
                                    <GridBox
                                        gridItemId={item?.gridItem?.id}
                                        title={item.title}
                                        gridAssets={gridAssets}
                                        isEditMode={isEditMode}
                                        editModalOpen={editModalOpen === item?.gridItem?.id} // nur Modal für dieses Item öffnen
                                        onEditOpen={() => setEditModalOpen(item?.gridItem?.id || null)}
                                        onEditClose={() => setEditModalOpen(null)}
                                        isVisible={isVisible}
                                    />
                            }
                        </Box>
                    })
                }
            </ResponsiveGridLayout>
        </Box>
    );
};

export default GridContent;
